'use client'

import { ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft } from 'lucide-react'
import { SecurityBadge, ContextualTestimonial } from '../ui/ContextualTestimonial'

interface QuizCardProps {
  icon?: string | ReactNode
  question: string
  subtitle?: string
  children: ReactNode
  stepKey: string
  onBack?: () => void
  canGoBack?: boolean
  timeEstimate?: string
  showSecurityBadge?: boolean
  showTestimonial?: boolean
  size?: 'default' | 'large'
}

export function QuizCard({
  icon,
  question,
  subtitle,
  children,
  stepKey,
  onBack,
  canGoBack = false,
  timeEstimate,
  showSecurityBadge = false,
  showTestimonial = false,
  size = 'default'
}: QuizCardProps) {
  const sizeClasses = {
    default: 'max-w-xl',
    large: 'max-w-2xl'
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={stepKey}
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -30, scale: 0.95 }}
        transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }}
        className={`w-full ${sizeClasses[size]} mx-auto`}
      >
        <div className="relative p-4 sm:p-5 md:p-6 rounded-2xl sm:rounded-3xl bg-white/85 dark:bg-white/5 backdrop-blur-xl border border-purple-500/20 dark:border-white/10 shadow-2xl">
          {/* Gradient glow effect - interno para evitar overflow */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-purple-500/10 to-transparent pointer-events-none" />

          {/* Header com botão voltar e tempo estimado */}
          {(canGoBack || timeEstimate) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-between mb-4 -mt-2"
            >
              {canGoBack && onBack ? (
                <button
                  onClick={onBack}
                  className="flex items-center gap-1.5 text-indigo-950/40 dark:text-white/40 hover:text-indigo-950/70 dark:hover:text-white/70 transition-colors text-sm group"
                  aria-label="Voltar para etapa anterior"
                >
                  <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
                  <span>Voltar</span>
                </button>
              ) : (
                <div />
              )}

              {timeEstimate && (
                <span className="text-indigo-950/30 dark:text-white/30 text-xs">
                  {timeEstimate}
                </span>
              )}
            </motion.div>
          )}

          {/* Icon */}
          {icon && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
              className={`flex justify-center ${size === 'large' ? 'mb-3' : 'mb-6'}`}
            >
              {typeof icon === 'string' ? (
                <span className={size === 'large' ? 'text-3xl md:text-4xl' : 'text-5xl md:text-6xl'}>{icon}</span>
              ) : (
                icon
              )}
            </motion.div>
          )}

          {/* Question */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`font-semibold text-indigo-950 dark:text-white text-center ${size === 'large' ? 'text-xl md:text-2xl mb-1' : 'text-2xl md:text-3xl mb-2'}`}
          >
            {question}
          </motion.h2>

          {/* Subtitle */}
          {subtitle && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className={`text-indigo-950/50 dark:text-white/50 text-center ${size === 'large' ? 'text-sm mb-3' : 'mb-6'}`}
            >
              {subtitle}
            </motion.p>
          )}

          {/* Content (input, options, etc) */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={size === 'large' ? 'mt-2' : 'mt-6'}
          >
            {children}
          </motion.div>

          {/* Security Badge */}
          {showSecurityBadge && (
            <SecurityBadge variant="minimal" className="mt-6" />
          )}

          {/* Contextual Testimonial */}
          {showTestimonial && (
            <ContextualTestimonial step={stepKey} className="mt-6" />
          )}

          {/* Keyboard shortcuts hint - hidden on large cards */}
          {size !== 'large' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-6 pt-4 border-t border-purple-500/20 dark:border-white/10"
            >
              <div className="flex items-center justify-center gap-4 text-[10px] text-indigo-950/20 dark:text-white/20">
                {canGoBack && (
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-indigo-950/5 dark:bg-white/10 rounded text-indigo-950/30 dark:text-white/30 font-mono">Esc</kbd>
                    voltar
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-indigo-950/5 dark:bg-white/10 rounded text-indigo-950/30 dark:text-white/30 font-mono">Enter</kbd>
                  continuar
                </span>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
